import { queryClient } from "@/config/react-query";

import {
  type BackgroundFill,
  computePersonMaskForImage,
} from "@/lib/photo-background";

import type {
  AttachmentStudioState,
  AttachmentStudioSummary,
  PhotoStudioSettings,
  StudioBackdrop,
} from "@/types/photo-studio";

import { getSignedUrlBase64 } from "@/api/auth/gcs";
import {
  requestStudioRefine,
  setAttachmentBackdrop,
  uploadStudioMask,
} from "@/api/provider/photo-studio/studio";
import {
  invalidateStudioRenders,
  photoStudioQueryOptions,
  setAttachmentStudioState,
} from "@/api/provider/photo-studio/studio-query";

// Studio Mode helpers shared by the camera, the editor and the viewer. They
// only ever send a cutout mask and a backdrop choice: the photo itself is
// uploaded untouched and the server keeps the original.

// Matches the server: Studio only works on these. PDFs, videos, DNG etc. are
// left alone.
const STUDIO_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];

// The usual light/grey checkerboard for "transparent", as a CSS background.
export const TRANSPARENT_CHECKER =
  "repeating-conic-gradient(#d4d4d4 0% 25%, #ffffff 0% 50%) 50% / 8px 8px";

// Studio saves a photo on the Transparent backdrop as `studio/<id>/render_*.png`
// (every other backdrop is a JPEG), so its URL says it has see-through parts.
export function isTransparentStudioRender(url: string | null | undefined) {
  if (!url) return false;
  const path = url.split("?")[0];
  return /\/studio\/[^/]+\/render_[^/]+\.png$/i.test(path);
}

export function isStudioImagePath(path: string | null | undefined): boolean {
  if (!path) return false;
  const lower = path.split("?")[0].toLowerCase();
  return STUDIO_IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

// The photo is shown on a backdrop, or has a cutout ready for one.
export function isStudioPhoto(state: AttachmentStudioSummary): boolean {
  return (
    state.hasMask || (state.backdrop !== null && state.backdrop !== "NONE")
  );
}

// A photo taken with Studio on gets the clinic default, or transparent when the
// clinic has not picked one.
export function backdropForNewPhoto(
  settings: PhotoStudioSettings | undefined,
): StudioBackdrop {
  const clinicDefault = settings?.defaultBackdrop ?? "NONE";
  return clinicDefault === "NONE" ? "TRANSPARENT" : clinicDefault;
}

// How the camera preview paints the background so it matches the backdrop the
// photo will get. Blur has no live preview and shows black.
export function previewFillForBackdrop(
  backdrop: StudioBackdrop,
  settings: PhotoStudioSettings | undefined,
): BackgroundFill | undefined {
  switch (backdrop) {
    case "TRANSPARENT":
      return "checker";
    case "WHITE":
      return "#ffffff";
    case "GREY":
      return "#808080";
    case "BRAND":
      return settings?.effectiveBrandColor ?? "#808080";
    default:
      return undefined;
  }
}

export function canRefine(settings: PhotoStudioSettings | undefined): boolean {
  return !!settings?.autoRefine && !!settings.cutoutServiceAvailable;
}

export async function loadStudioSettings(): Promise<
  PhotoStudioSettings | undefined
> {
  try {
    return await queryClient.fetchQuery(photoStudioQueryOptions.settings());
  } catch {
    return undefined;
  }
}

async function refineIfEnabled(
  attachmentId: string,
  settings: PhotoStudioSettings | undefined,
): Promise<void> {
  if (!canRefine(settings)) return;
  const refined = await requestStudioRefine(attachmentId);
  if (refined) setAttachmentStudioState(refined);
}

async function saveMask(
  attachmentId: string,
  mask: Blob,
  gcsPath: string,
): Promise<AttachmentStudioState | null> {
  const state = await uploadStudioMask(attachmentId, mask, gcsPath);
  if (state) {
    setAttachmentStudioState(state);
    void invalidateStudioRenders(attachmentId);
  }
  return state;
}

/**
 * After a Studio capture is uploaded: store the device cutout next to the
 * original, pick the backdrop and ask the server for a sharper cutout. Each
 * step is best effort; the original photo is already safely saved.
 */
export async function applyStudioToNewPhoto(
  attachment: { id: string; gcsPath?: string | null },
  mask: Blob,
): Promise<void> {
  if (!attachment.gcsPath) return;
  try {
    const settings = await loadStudioSettings();
    const masked = await saveMask(attachment.id, mask, attachment.gcsPath);
    if (!masked) return;
    const withBackdrop = await setAttachmentBackdrop(
      attachment.id,
      backdropForNewPhoto(settings),
    );
    if (withBackdrop) {
      setAttachmentStudioState(withBackdrop);
      // saveMask refreshed the gallery thumbnails before the backdrop was set,
      // when the photo had no Studio version yet; refresh them again.
      void invalidateStudioRenders(attachment.id);
    }
    await refineIfEnabled(attachment.id, settings);
  } catch (error) {
    console.error("Studio setup failed for a new photo:", error);
  }
}

/**
 * After rotate/crop the old cutout no longer lines up and the server drops it.
 * Recompute it from the edited image (already on a canvas) for the new path.
 */
export async function restudioEditedPhoto(
  attachmentId: string,
  gcsPath: string,
  mask: Blob | null,
): Promise<void> {
  try {
    const settings = await loadStudioSettings();
    if (mask) await saveMask(attachmentId, mask, gcsPath);
    await refineIfEnabled(attachmentId, settings);
  } catch (error) {
    console.error("Studio update failed after editing a photo:", error);
  }
}

async function loadAttachmentImage(gcsPath: string): Promise<HTMLImageElement> {
  // Same-origin data URL, so the canvas can read the pixels without CORS.
  const response = await getSignedUrlBase64(gcsPath);
  if (!response?.signedUrl) throw new Error("Failed to load photo");
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("Failed to load photo"));
    image.src = response.signedUrl;
  });
  return image;
}

/**
 * Photos uploaded from a file, or taken before Studio, have no cutout yet.
 * Make one on this device so a backdrop can be applied straight away, and ask
 * the server to sharpen it. Returns false when nobody is found in the photo.
 */
export async function ensureStudioMask(
  state: AttachmentStudioState,
): Promise<boolean> {
  if (!state.isImage) return false;
  if (state.hasMask) return true;
  const image = await loadAttachmentImage(state.gcsPath);
  const mask = await computePersonMaskForImage(image);
  if (!mask) return false;
  const saved = await saveMask(state.attachmentId, mask, state.gcsPath);
  if (!saved?.hasMask) return false;
  await refineIfEnabled(state.attachmentId, await loadStudioSettings());
  return true;
}
